import { Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { filter, map, Observable, Subject } from 'rxjs';

export enum WebSocketConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  AUTHENTICATED = 'authenticated',
  ERROR = 'error',
}

interface SocketOptions {
  token?: string;
}

interface SocketEntry {
  url: string;
  token?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private readonly _reconnectDelay = 10000;
  private readonly _pingInterval = 30000;

  private readonly _sockets = new Map<string, WebSocket>();
  private readonly _streams = new Map<string, Subject<unknown>>();
  private readonly _states = new Map<string, WritableSignal<WebSocketConnectionState>>();
  private readonly _pingTimers = new Map<string, ReturnType<typeof setInterval>>();
  private readonly _reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _reconnectEntries = new Map<string, SocketEntry>();
  private readonly _intentionalClose = new Set<string>();
  private readonly _closes = new Subject<{ id: string; code: number }>();

  /**
   * Open a named WebSocket connection.
   * If a token is provided, sends an authentication message immediately after connecting.
   * Safe to call multiple times — no-ops if the socket is already open or connecting.
   */
  connect(id: string, url: string, options?: SocketOptions): void {
    const socket = this._sockets.get(id);
    if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this._reconnectEntries.set(id, { url, token: options?.token });
    this._intentionalClose.delete(id);

    const existingStream = this._streams.get(id);
    if (!existingStream || existingStream.closed) {
      this._streams.set(id, new Subject<unknown>());
    }

    this._connectSocket(id);
  }

  /**
   * Close a named WebSocket connection permanently.
   * Completes and removes the message stream, preventing reconnects.
   */
  disconnect(id: string): void {
    this._intentionalClose.add(id);
    this._cleanupSocket(id);
    this._getState(id).set(WebSocketConnectionState.DISCONNECTED);
    this._streams.get(id)?.complete();
    this._streams.delete(id);
    this._states.delete(id);
    this._reconnectEntries.delete(id);
    this._intentionalClose.delete(id);
  }

  /**
   * Returns a typed Observable of all non-system messages for a given socket.
   * The stream survives reconnects — subscribers do not need to re-subscribe.
   */
  messages$<T = unknown>(id: string): Observable<T> {
    if (!this._streams.has(id)) {
      this._streams.set(id, new Subject<unknown>());
    }
    return this._streams.get(id)!.asObservable() as Observable<T>;
  }

  /**
   * The close code each time a socket closes without `disconnect()`. A code
   * of 4000 or more is the server refusing it (an invalid token, a user without
   * access): no automatic reconnect follows, so the owner decides what to do.
   */
  closes$(id: string): Observable<number> {
    return this._closes.pipe(
      filter((close) => close.id === id),
      map((close) => close.code),
    );
  }

  ngOnDestroy(): void {
    const ids = new Set([...this._sockets.keys(), ...this._reconnectEntries.keys()]);
    for (const id of ids) {
      this.disconnect(id);
    }
  }

  // ==================== PRIVATE METHODS ====================

  private _getState(id: string): WritableSignal<WebSocketConnectionState> {
    if (!this._states.has(id)) {
      this._states.set(id, signal(WebSocketConnectionState.DISCONNECTED));
    }
    return this._states.get(id)!;
  }

  private _connectSocket(id: string): void {
    const entry = this._reconnectEntries.get(id);
    if (!entry) return;

    this._cleanupSocket(id);
    this._getState(id).set(WebSocketConnectionState.CONNECTING);

    try {
      const socket = new WebSocket(entry.url);
      this._sockets.set(id, socket);

      socket.onopen = () => {
        this._getState(id).set(WebSocketConnectionState.CONNECTED);
        if (entry.token) {
          socket.send(JSON.stringify({ type: 'authenticate', token: entry.token }));
        }
      };

      socket.onmessage = (event: MessageEvent) => {
        this._handleMessage(id, event, socket);
      };

      socket.onclose = (event: CloseEvent) => {
        this._stopPing(id);
        this._getState(id).set(WebSocketConnectionState.DISCONNECTED);
        if (this._intentionalClose.has(id)) return;
        if (event.code !== 1000 && event.code < 4000) this._scheduleReconnect(id);
        this._closes.next({ id, code: event.code });
      };

      socket.onerror = () => {
        this._getState(id).set(WebSocketConnectionState.ERROR);
      };
    } catch (error) {
      console.error(`[WebSocket:${id}] Failed to create connection:`, error);
      this._getState(id).set(WebSocketConnectionState.ERROR);
      this._scheduleReconnect(id);
    }
  }

  private _handleMessage(id: string, event: MessageEvent, socket: WebSocket): void {
    let data: unknown;
    try {
      data = JSON.parse(event.data as string);
    } catch {
      return;
    }

    if (!data || typeof data !== 'object') return;
    const msg = data as Record<string, unknown>;

    if (msg['type'] === 'authenticated') {
      this._getState(id).set(WebSocketConnectionState.AUTHENTICATED);
      this._startPing(id, socket);
      return;
    }

    if (msg['type'] === 'pong' || msg['type'] === 'connection.established') return;

    if (msg['type'] === 'error') {
      console.error(`[WebSocket:${id}] Server error:`, msg['error']);
      return;
    }

    const stream = this._streams.get(id);
    if (stream && !stream.closed) {
      stream.next(data);
    }
  }

  private _startPing(id: string, socket: WebSocket): void {
    this._stopPing(id);
    this._pingTimers.set(
      id,
      setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, this._pingInterval),
    );
  }

  private _stopPing(id: string): void {
    const timer = this._pingTimers.get(id);
    if (timer) {
      clearInterval(timer);
      this._pingTimers.delete(id);
    }
  }

  private _scheduleReconnect(id: string): void {
    if (this._intentionalClose.has(id)) return;
    this._reconnectTimers.set(
      id,
      setTimeout(() => {
        this._reconnectTimers.delete(id);
        this._connectSocket(id);
      }, this._reconnectDelay),
    );
  }

  private _cleanupSocket(id: string): void {
    this._stopPing(id);

    const timer = this._reconnectTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this._reconnectTimers.delete(id);
    }

    const socket = this._sockets.get(id);
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onclose = null;
      socket.onerror = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close(1000, 'Client disconnect');
      }
      this._sockets.delete(id);
    }
  }
}
